import {
  woundUp,
  type BattleEvent,
  type Fighter,
  type FighterId,
  type Roster,
  type Vec3,
} from '@archon3d/core';
import type { AnimationGroup } from '@babylonjs/core/Animations/animationGroup';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import type { AbstractMesh } from '@babylonjs/core/Meshes/abstractMesh';
import type { Mesh } from '@babylonjs/core/Meshes/mesh';
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import type { Scene } from '@babylonjs/core/scene';
import type { CharacterInstance, CharacterModelCache } from '../board/characterModels';
import type { FighterPose } from './battleController';
import { BURN_CLEARANCE, BurnRing } from './burnRing';

type Clip = 'idle' | 'walk' | 'run' | 'fly' | 'attack' | 'hit' | 'death';

export interface FighterViewOptions {
  scene: Scene;
  models: CharacterModelCache;
  roster: Roster;
  fighter: Fighter;
  parent: TransformNode;
  /** Faction colour (`#rrggbb`) for the stand-in capsule and a death's fading light. */
  color: string;
  /** Called for every mesh that appears (shadows, light lists). */
  onMesh?: (mesh: AbstractMesh) => void;
  /** Where a fallen body's dust and its fading light are drawn: the duel's sparks. */
  effects?: DeathEffects;
}

/** A death's effects, in the arena's own space: the dust of the fall, the motes of the fade. */
export interface DeathEffects {
  dust(from: Vec3, to: Vec3): void;
  motes(from: Vec3, to: Vec3, color: string): void;
}

const FLASH_SECONDS = 0.14;
/**
 * A blow lands: the struck fighter holds still this long, pose and clip, the pause that makes a
 * hit read as one (x-aaa-gap#5). The simulation goes on meanwhile, so the body then catches up
 * with it over a moment rather than jumping.
 */
export const HIT_STOP_SECONDS = 0.07;
const CATCH_UP_SECONDS = 0.06;
const FLASH_COLOR = new Color3(0.9, 0.25, 0.2);
/**
 * Burning ground and the closing ring of sudden death take health every step: the body glows the
 * fire's colour and flickers with it while they do (client-arena:quality#9), where it used to show
 * the same red as a blow for as long as it burned.
 */
const BURN_COLOR = new Color3(1, 0.42, 0.06);
const BURN_SECONDS = 0.25;
const RUN_SPEED = 3.5;
const WALK_SPEED = 0.4;
/**
 * How quickly a body comes round to a new facing, per second: most of a turn in a tenth of a
 * second. A fighter turns the way it moves (the owner, 2026-09-26), so a key pressed the other way
 * turns it right round, which drawn in one frame read as a jump rather than a turn.
 */
const TURN_RATE = 20;
/**
 * A body without clips of its own moves by itself (the owner, 2026-09-26: a fighter is seen to
 * walk and run). A walker steps, a bounce on each foot and its weight going from one to the other,
 * a step to so much ground covered, so its steps keep time with its pace; a flier hovers, rising
 * and falling on the air whether it goes or stands. Both lean the way they go, and settle as they
 * stop rather than freeze mid-step. Lengths are shares of the fighter's height.
 */
const STEP_LENGTH = 0.8;
const STEP_BOUNCE = 0.04;
const STEP_ROLL = 0.09;
const WALK_LEAN = 0.14;
const FLY_LEAN = 0.3;
const HOVER_RATE = 2.2;
const HOVER_HEIGHT = 0.05;
const GAIT_EASE = 8;
/** Mixamo swings and reactions run long for a fast duel; longer clips are played faster to fit. */
const ONE_SHOT_MAX_SECONDS: Partial<Record<Clip, number>> = { attack: 0.9, hit: 0.4 };
/**
 * A death (x-aaa-gap#8), in seconds of the duel as shown. A body without a death clip falls on
 * its back, gathering speed, and lands on the ground rather than into it; one with a clip plays
 * it. Dust rises where it lands, and a moment later the body fades away in motes of its side's
 * colour, where it used to lie still until the arena went dark.
 */
const FALL_SECONDS = 0.6;
const FADE_AFTER_FALL = 1.1;
/** A death clip's body is down at about this part of it; the fade waits for that, within bounds. */
const CLIP_LANDS_AT = 0.7;
const CLIP_FADE_AFTER: [number, number] = [1.4, 2.4];
const FADE_SECONDS = 1;

interface EmissiveMaterial {
  emissiveColor: Color3;
}

/**
 * One fighter at true scale: the character's model when it has one (a capsule until it loads),
 * animated from the simulation – clips when the model brings them, small procedural motions
 * otherwise – plus a red flash when hit.
 */
export class FighterView {
  readonly id: FighterId;
  readonly root: TransformNode;
  private placeholder: Mesh | null;
  private instance: CharacterInstance | null = null;
  private readonly materials: { material: EmissiveMaterial; emissive: Color3 }[] = [];
  private oneShotUntil = 0;
  private locomotion: 'idle' | 'walk' | 'run' | null = null;
  private flashLeft = 0;
  private burnLeft = 0;
  private tinted = false;
  private stopLeft = 0;
  private catchUpLeft = 0;
  /** Where the hit-stop left the body, for it to catch up from. */
  private readonly heldAt = { x: 0, y: 0, z: 0 };
  /** The clip playing now and the pace it plays at, for the hit-stop to hold and let go. */
  private group: AnimationGroup | null = null;
  private groupSpeed = 1;
  private dead = false;
  /** Whether the death is the model's own clip; without one the body falls by itself. */
  private dyingByClip = false;
  /** How long the death's clip runs as shown, when it has one. */
  private deathSeconds = 0;
  /** Seconds of the death so far, from the end of the killing blow's pause. */
  private deathTime = 0;
  private landed = false;
  private fading = false;
  /** How far the body reaches behind its pivot, and where it stood: measured as it starts to fall. */
  private behind: number | null = null;
  private fallZ = 0;
  private fallen = 0;
  private lunge = 0;
  /** The procedural gait: the steps taken so far (π a step) and the pace ahead and aside, eased. */
  private stride = 0;
  private ahead = 0;
  private aside = 0;
  /** Whether the body has been placed once; the first placement faces it at once, unturned. */
  private placed = false;
  private clock = 0;
  private disposed = false;
  /** Only a ghost has one; everyone else never builds it. */
  private readonly burnRing: BurnRing | null;

  constructor(private readonly options: FighterViewOptions) {
    const { scene, fighter, parent, roster, models } = options;
    this.id = fighter.id;
    this.root = new TransformNode(`fighter-${fighter.id}`, scene);
    this.burnRing =
      fighter.attackType === 'burn'
        ? new BurnRing(scene, this.root, fighter.range, fighter.halfWidth + BURN_CLEARANCE)
        : null;
    this.root.parent = parent;

    const capsule = MeshBuilder.CreateCapsule(
      `fighter-${fighter.id}-capsule`,
      { height: fighter.height, radius: fighter.halfWidth, tessellation: 12, subdivisions: 2 },
      scene,
    );
    capsule.position.y = fighter.height / 2;
    capsule.parent = this.root;
    capsule.isPickable = false;
    const material = new StandardMaterial(`fighter-${fighter.id}-capsule`, scene);
    material.diffuseColor = Color3.FromHexString(options.color);
    capsule.material = material;
    this.placeholder = capsule;
    this.materials.push({ material, emissive: material.emissiveColor.clone() });
    options.onMesh?.(capsule);

    const model = roster[fighter.character]?.model;
    if (!model) return;
    void models
      // Its own materials: a flash or a fade on one fighter must not show on the other, who
      // may be the same character (client-arena:bugs#7).
      .instantiate(model, `fighter-${fighter.id}-model`, {
        height: fighter.height,
        ownMaterials: true,
      })
      .then((instance) => {
        if (this.disposed) {
          instance.dispose();
          return;
        }
        instance.root.parent = this.root;
        this.instance = instance;
        this.materials.length = 0;
        for (const mesh of instance.meshes) {
          mesh.isPickable = false;
          options.onMesh?.(mesh);
          const m = mesh.material as unknown as EmissiveMaterial | null;
          if (m?.emissiveColor && !this.materials.some((entry) => entry.material === m))
            this.materials.push({ material: m, emissive: m.emissiveColor.clone() });
        }
        // Hidden rather than disposed: it stays in the shadow caster list until the view goes.
        this.placeholder?.setEnabled(false);
        this.locomotion = null;
        if (this.dead) this.die();
      })
      .catch((err: unknown) => console.warn(`model for ${fighter.character} failed to load`, err));
  }

  get visible(): boolean {
    return this.root.isEnabled();
  }

  set visible(value: boolean) {
    this.root.setEnabled(value);
  }

  /** Call once per rendered frame. */
  update(pose: FighterPose, fighter: Fighter, events: BattleEvent[], dt: number): void {
    this.clock += dt;
    if (this.stopLeft <= 0) this.place(pose, dt);
    this.burnRing?.update(fighter.burning, dt);

    for (const event of events) {
      // A blow's swing starts as it is wound up and lands a moment later (D-42); a shot's is its
      // release.
      const swings =
        (event.type === 'windup' || (event.type === 'attack' && !woundUp(event.kind))) &&
        event.fighter === this.id;
      if (swings) {
        if (!this.play('attack', false)) this.lunge = 0.18;
      } else if (event.type === 'hit' && event.target === this.id && event.source !== 'cost') {
        // A burst's cost to its own owner is no wound. Everything else shows; only a blow
        // staggers (D-34), so only a blow plays the flinch.
        if (event.source === 'hazard') this.burnLeft = BURN_SECONDS;
        else this.flashLeft = FLASH_SECONDS;
        if (event.source === 'blow') this.stop();
      } else if (event.type === 'death' && event.fighter === this.id) {
        this.dead = true;
        this.die();
      }
    }

    if (this.stopLeft > 0) {
      this.stopLeft -= dt;
      if (this.group) this.group.speedRatio = this.stopLeft > 0 ? 0 : this.groupSpeed;
      if (this.stopLeft <= 0) {
        const { position } = this.root;
        Object.assign(this.heldAt, { x: position.x, y: position.y, z: position.z });
        this.catchUpLeft = CATCH_UP_SECONDS;
      }
    }

    const tinted = this.flashLeft > 0 || this.burnLeft > 0;
    if (tinted || this.tinted) {
      this.flashLeft = Math.max(0, this.flashLeft - dt);
      this.burnLeft = Math.max(0, this.burnLeft - dt);
      const flash = this.flashLeft / FLASH_SECONDS;
      const burn =
        this.burnLeft > 0
          ? (0.5 + 0.25 * Math.sin(this.clock * 15)) * Math.min(1, this.burnLeft / 0.1)
          : 0;
      for (const { material, emissive } of this.materials) {
        const fire = burn > 0 ? Color3.Lerp(emissive, BURN_COLOR, burn) : emissive;
        material.emissiveColor = flash > 0 ? Color3.Lerp(fire, FLASH_COLOR, flash) : fire;
      }
      // One more frame after the last tint puts every material back as it was.
      this.tinted = tinted;
    }

    const body = this.instance?.root ?? (this.placeholder?.isEnabled() ? this.placeholder : null);
    if (this.dead) {
      if (body && this.stopLeft <= 0) this.showDeath(body, dt);
      return;
    }

    if (this.oneShotUntil <= this.clock) {
      const speed = Math.hypot(fighter.vel.x, fighter.vel.z);
      const wanted = speed > RUN_SPEED ? 'run' : speed > WALK_SPEED ? 'walk' : 'idle';
      if (wanted !== this.locomotion) {
        this.locomotion = wanted;
        // Fliers keep flapping on the ground; a walker without a walk clip jogs.
        if (
          !this.play(wanted, true) &&
          !(wanted !== 'idle' && (this.play('fly', true) || this.play('run', true)))
        )
          this.play('idle', true);
      }
    }

    // A body without clips walks or hovers by itself, and lunges when it attacks. It holds still
    // through a hit-stop, as a clip would.
    if (body && !this.instance?.animations.size && this.stopLeft <= 0)
      this.moveBody(body, fighter, dt);
  }

  /**
   * The gait of a body without clips (see `STEP_LENGTH`), from the fighter's velocity in its own
   * frame: ahead and aside of the way it faces. The capsule stands on its middle, a model on its
   * feet.
   */
  private moveBody(body: TransformNode, fighter: Fighter, dt: number): void {
    const { height, movement } = this.options.fighter;
    const yaw = this.root.rotation.y;
    const { x, z } = fighter.vel;
    const ease = 1 - Math.exp(-GAIT_EASE * dt);
    const share = (v: number) => Math.max(-1, Math.min(1, v / RUN_SPEED));
    this.ahead += (share(x * Math.sin(yaw) + z * Math.cos(yaw)) - this.ahead) * ease;
    this.aside += (share(x * Math.cos(yaw) - z * Math.sin(yaw)) - this.aside) * ease;
    let rise: number;
    // Leaning ahead tips the head towards +z, aside towards +x; a turn about z the other way.
    if (movement === 'fly') {
      rise = Math.sin(this.clock * HOVER_RATE) * HOVER_HEIGHT * height;
      body.rotation.x = this.ahead * FLY_LEAN;
      body.rotation.z = -this.aside * FLY_LEAN;
    } else {
      this.stride += ((Math.hypot(x, z) * dt) / (STEP_LENGTH * height)) * Math.PI;
      const step = Math.sin(this.stride);
      const going = Math.min(1, Math.hypot(this.ahead, this.aside));
      rise = Math.abs(step) * STEP_BOUNCE * height * going;
      body.rotation.x = this.ahead * WALK_LEAN;
      body.rotation.z = step * STEP_ROLL * going - this.aside * WALK_LEAN;
    }
    this.lunge = Math.max(0, this.lunge - dt);
    body.position.y = this.standingY(body) + rise;
    body.position.z = this.lunge > 0 ? Math.sin((this.lunge / 0.18) * Math.PI) * 0.35 : 0;
  }

  dispose(): void {
    this.disposed = true;
    for (const { material, emissive } of this.materials) material.emissiveColor = emissive;
    this.burnRing?.dispose();
    this.instance?.dispose();
    // The capsule's material is its own, so it goes with it.
    this.placeholder?.dispose(false, true);
    this.root.dispose();
  }

  /**
   * Draws the body where the simulation has it, or on its way there after a hit-stop, turning to
   * the way it faces over a moment (`TURN_RATE`), the short way round.
   */
  private place(pose: FighterPose, dt: number): void {
    const { position, rotation } = this.root;
    const turn = Math.atan2(Math.sin(pose.yaw - rotation.y), Math.cos(pose.yaw - rotation.y));
    rotation.y = this.placed ? rotation.y + turn * (1 - Math.exp(-TURN_RATE * dt)) : pose.yaw;
    this.placed = true;
    if (this.catchUpLeft <= 0) {
      position.set(pose.pos.x, pose.pos.y, pose.pos.z);
      return;
    }
    this.catchUpLeft = Math.max(0, this.catchUpLeft - dt);
    const t = 1 - this.catchUpLeft / CATCH_UP_SECONDS;
    const from = this.heldAt;
    position.set(
      from.x + (pose.pos.x - from.x) * t,
      from.y + (pose.pos.y - from.y) * t,
      from.z + (pose.pos.z - from.z) * t,
    );
  }

  /** A blow's pause: the body, its clip and a flinch just begun all hold still together. */
  private stop(): void {
    // A clip held still ends that much later, so locomotion waits for it.
    const added = HIT_STOP_SECONDS - Math.max(0, this.stopLeft);
    // (A dead body's is endless and stays so.)
    if (this.oneShotUntil > this.clock) this.oneShotUntil += added;
    else if (this.play('hit', false)) this.oneShotUntil += HIT_STOP_SECONDS;
    this.stopLeft = HIT_STOP_SECONDS;
    this.catchUpLeft = 0;
  }

  /** Where a body's pivot stands: a model's at its feet, the capsule's at its middle. */
  private standingY(body: TransformNode): number {
    return body === this.placeholder ? this.options.fighter.height / 2 : 0;
  }

  /**
   * The death begins, or begins again on a model that arrived after it: the model's clip if it
   * has one, or else its pose held for the body to fall by itself.
   */
  private die(): void {
    this.oneShotUntil = Infinity;
    const body = this.instance?.root ?? this.placeholder;
    if (body && !this.instance?.animations.size) body.rotation.z = 0;
    // Measured again on the body now shown: a model may take over from the capsule mid-fall.
    this.behind = null;
    this.dyingByClip = !!this.play('death', false);
    if (this.dyingByClip && this.group) {
      const fps = this.group.targetedAnimations[0]?.animation.framePerSecond ?? 30;
      this.deathSeconds = (this.group.to - this.group.from) / fps / (this.groupSpeed || 1);
    } else this.group?.pause();
  }

  /** A dead body falls, raises its dust, and fades into its side's light. */
  private showDeath(body: TransformNode, dt: number): void {
    this.deathTime += dt;
    if (!this.dyingByClip) {
      if (this.behind === null) {
        this.behind = reachBehind(body, this.root);
        this.fallZ = body.position.z;
      }
      this.fallen = Math.min(1, this.fallen + dt / FALL_SECONDS);
      // Over backwards about its feet, gathering speed as it goes; lifted by what it has behind
      // them, so its back ends on the ground rather than in it. The capsule's pivot is its middle,
      // which goes over with it.
      const angle = (Math.PI / 2) * this.fallen * this.fallen;
      const standing = this.standingY(body);
      body.rotation.x = -angle;
      body.position.y = standing * Math.cos(angle) + this.behind * Math.sin(angle);
      body.position.z = this.fallZ - standing * Math.sin(angle);
    }
    const landsAt = this.dyingByClip ? this.deathSeconds * CLIP_LANDS_AT : FALL_SECONDS;
    const fadeAt = this.dyingByClip
      ? Math.min(CLIP_FADE_AFTER[1], Math.max(CLIP_FADE_AFTER[0], this.deathSeconds * 0.9))
      : FADE_AFTER_FALL;
    if (!this.landed && this.deathTime >= landsAt) {
      this.landed = true;
      if (this.deathTime < fadeAt + FADE_SECONDS)
        this.options.effects?.dust(...this.lengthOf(body));
    }
    if (this.deathTime < fadeAt) return;
    if (!this.fading) {
      this.fading = true;
      this.options.effects?.motes(...this.lengthOf(body), this.options.color);
    }
    const left = 1 - Math.min(1, (this.deathTime - fadeAt) / FADE_SECONDS);
    const meshes = this.instance?.meshes ?? (this.placeholder ? [this.placeholder] : []);
    for (const mesh of meshes) mesh.visibility = left * left;
    // Gone, shadow and all.
    if (left <= 0) body.setEnabled(false);
  }

  /**
   * The ground a fallen body covers, in the arena's space: from its feet to its head, on its back
   * behind where it stood. A clip may fall either way, so its length is taken both ways.
   */
  private lengthOf(body: TransformNode): [Vec3, Vec3] {
    const { position, rotation } = this.root;
    const yaw = rotation.y + (body === this.placeholder ? 0 : body.rotation.y);
    const reach = this.options.fighter.height * 0.9;
    const back = { x: -Math.sin(yaw) * reach, z: -Math.cos(yaw) * reach };
    const at = (k: number) => ({
      x: position.x + back.x * k,
      y: position.y + 0.05,
      z: position.z + back.z * k,
    });
    return this.dyingByClip ? [at(-0.5), at(0.5)] : [at(0), at(1)];
  }

  /** Starts a clip if the model has it; one-shots block locomotion until they end. */
  private play(clip: Clip, loop: boolean): true | undefined {
    const group = this.instance?.play(clip, loop);
    if (!group) return undefined;
    if (!loop) {
      const fps = group.targetedAnimations[0]?.animation.framePerSecond ?? 30;
      const natural = Math.max(0.1, (group.to - group.from) / fps);
      const limit = ONE_SHOT_MAX_SECONDS[clip];
      const seconds = limit ? Math.min(natural, limit) : natural;
      group.speedRatio = natural / seconds;
      this.oneShotUntil = clip === 'death' ? Infinity : this.clock + seconds;
      this.locomotion = null;
    } else group.speedRatio = 1;
    this.group = group;
    this.groupSpeed = group.speedRatio;
    // A clip that starts inside a hit-stop waits it out too.
    if (this.stopLeft > 0) group.speedRatio = 0;
    return true;
  }

  /** Whether a hit-stop holds the fighter still now; for tests. */
  get held(): boolean {
    return this.stopLeft > 0;
  }

  /** Whether the fire's glow is on the body now; for tests. */
  get burning(): boolean {
    return this.burnLeft > 0;
  }
}

/**
 * How far a standing body reaches behind its pivot, in metres of the fighter's own frame: what it
 * lies on once it has fallen on its back about its feet. Its meshes' boxes, turned as the body is.
 */
function reachBehind(body: TransformNode, fighter: TransformNode): number {
  const toFighter = fighter.computeWorldMatrix(true).clone().invert();
  const yaw = body.rotation.y;
  const behind = new Vector3(-Math.sin(yaw), 0, -Math.cos(yaw));
  // The capsule is itself the body; a model is the meshes under its root.
  const own = 'getBoundingInfo' in body ? [body as AbstractMesh] : [];
  let most = 0;
  const corner = new Vector3();
  for (const mesh of [...own, ...body.getChildMeshes(false)]) {
    const local = mesh.computeWorldMatrix(true).multiply(toFighter);
    for (const vector of mesh.getBoundingInfo().boundingBox.vectors) {
      Vector3.TransformCoordinatesToRef(vector, local, corner);
      most = Math.max(most, corner.subtract(body.position).dot(behind));
    }
  }
  return most;
}
